import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CUSTOMER_SHELL_ICON_BUTTON_CLASS } from '../../app/customerTopBarStyles.js';

export interface CustomerBackLinkProps {
  /** Named parent path. Required unless `onBack` is provided. */
  readonly to?: string;
  /** In-flow step back (checkout). Prefer `to` when a real route parent exists. */
  readonly onBack?: () => void;
  readonly label?: string;
  /**
   * When true, keep aria-label but hide visible “Zpět” text (shell top bar —
   * frees width so ← · title/place · Org · Cart · Settings · Profile never clip;
   * prefs live on the Settings sheet, not Profile).
   */
  readonly iconOnly?: boolean;
  readonly testId?: string;
  readonly className?: string;
  readonly disabled?: boolean;
}

const BACK_LINK_CLASS =
  'inline-flex min-h-11 items-center gap-1.5 rounded-md px-1 py-1 text-sm font-medium text-[var(--color-on-surface)] hover:bg-[var(--color-surface-elevated)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)] disabled:cursor-not-allowed disabled:opacity-40';

/** Left beak chevron (‹) — matches Obchody directory disclosure, not a shafted ←. */
function BackBeakChevron(): JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className="h-5 w-5 shrink-0 text-current"
    >
      <path
        d="M12 5.25 6.75 10 12 14.75"
        stroke="currentColor"
        strokeWidth="2.35"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Hierarchical ← in shell chrome (← · Org · Cart · Settings · Profile) — named `to` or in-flow `onBack`, never history.back(). */
export function CustomerBackLink({
  to,
  onBack,
  label,
  iconOnly = false,
  testId = 'customer-hierarchical-back',
  className,
  disabled = false,
}: CustomerBackLinkProps): JSX.Element {
  const { t } = useTranslation('customer');
  const resolvedLabel = label ?? t('nav.back');
  const baseClass = iconOnly ? CUSTOMER_SHELL_ICON_BUTTON_CLASS : BACK_LINK_CLASS;
  const resolvedClass =
    className !== undefined ? `${baseClass} ${className}` : baseClass;
  const labelClassName = iconOnly ? 'sr-only' : undefined;

  if (onBack !== undefined) {
    return (
      <button
        type="button"
        className={resolvedClass}
        data-testid={testId}
        aria-label={resolvedLabel}
        disabled={disabled}
        onClick={onBack}
      >
        <BackBeakChevron />
        <span className={labelClassName}>{resolvedLabel}</span>
      </button>
    );
  }

  if (to === undefined || to.length === 0) {
    throw new Error('CustomerBackLink requires `to` or `onBack`');
  }

  return (
    <Link
      to={to}
      className={resolvedClass}
      data-testid={testId}
      aria-label={resolvedLabel}
    >
      <BackBeakChevron />
      <span className={labelClassName}>{resolvedLabel}</span>
    </Link>
  );
}
